'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { IconBell, IconChecks, IconAt } from '@tabler/icons-react'

interface NotificationView {
  id: string
  type: 'assigned' | 'commented' | 'status_changed' | 'task_mention' | 'wiki_mention' | 'wiki_task' | 'reminder' | 'file_share'
  message: string
  created_at: string
  read_at: string | null
  actor_name: string
  project_code: string | null
  task_number: number | null
  task_title: string
  link: string | null
}

const STRINGS = {
  de: {
    types: {
      assigned: 'hat dir zugewiesen', commented: 'hat kommentiert', status_changed: 'hat den Status geändert', task_mention: 'hat dich erwähnt',
      wiki_mention: 'hat dich erwähnt', wiki_task: 'hat dir eine Aufgabe zugewiesen', reminder: 'erinnert dich an', file_share: 'hat Dateien mit dir geteilt',
    } as Record<string, string>,
    justNow: 'gerade eben', minutesAgo: (n: number) => `vor ${n} Min.`, hoursAgo: (n: number) => `vor ${n} Std.`,
    title: 'Benachrichtigungen', allRead: 'Alle gelesen', empty: 'Keine Benachrichtigungen',
  },
  en: {
    types: {
      assigned: 'assigned you', commented: 'commented on', status_changed: 'changed the status of', task_mention: 'mentioned you in',
      wiki_mention: 'mentioned you in', wiki_task: 'assigned you a task', reminder: 'reminds you of', file_share: 'shared files with you',
    } as Record<string, string>,
    justNow: 'just now', minutesAgo: (n: number) => `${n} min ago`, hoursAgo: (n: number) => `${n} h ago`,
    title: 'Notifications', allRead: 'Mark all read', empty: 'No notifications',
  },
}

const POLL_MS = 60_000

export default function NotificationBell({ locale, align = 'left' }: { locale: string; align?: 'left' | 'right' }) {
  const t = STRINGS[locale === 'en' ? 'en' : 'de']
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationView[]>([])
  const [unread, setUnread] = useState(0)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/projects/notifications')
      if (!r.ok) return
      const j = await r.json()
      setItems(j.notifications || [])
      setUnread(j.unread || 0)
    } catch {
      /* polling — silent */
    }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, POLL_MS)
    return () => clearInterval(t)
  }, [load])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const markAllRead = async () => {
    setUnread(0)
    setItems(curr => curr.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })))
    await fetch('/api/projects/notifications', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ all: true }),
    }).catch(() => {})
  }

  const markRead = (id: string) => {
    // Optimistic, fire-and-forget — the click continues to the task link.
    setItems(curr => curr.map(n => n.id === id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n))
    setUnread(u => Math.max(0, u - 1))
    fetch('/api/projects/notifications', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: [id] }),
    }).catch(() => {})
  }

  const timeAgo = (dateStr: string) => {
    const diffMins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000)
    if (diffMins < 1) return t.justNow
    if (diffMins < 60) return t.minutesAgo(diffMins)
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return t.hoursAgo(diffHours)
    return new Date(dateStr).toLocaleDateString(locale === 'en' ? 'en-GB' : 'de-DE', { day: '2-digit', month: '2-digit' })
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label={t.title}
      >
        <IconBell size={18} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full mt-1 w-80 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden`}>
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">{t.title}</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-xs text-teal-600 hover:underline"
              >
                <IconChecks size={14} /> {t.allRead}
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-gray-400">
                {t.empty}
              </div>
            ) : (
              items.map(n => (
                <a
                  key={n.id}
                  href={n.link
                    ? `/${locale}${n.link}`
                    : n.project_code && n.task_number
                      ? `/${locale}/projects/${n.project_code}/tasks/${n.task_number}`
                      : `/${locale}/projects`}
                  onClick={() => { if (!n.read_at) markRead(n.id) }}
                  className={`block px-3 py-2.5 border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                    !n.read_at ? 'bg-teal-50/50 dark:bg-teal-900/10' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read_at && <span className="w-2 h-2 mt-1.5 rounded-full bg-teal-500 shrink-0" />}
                    <div className={`flex-1 min-w-0 ${n.read_at ? 'pl-4' : ''}`}>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {n.type === 'task_mention' && <IconAt size={12} className="inline mr-0.5 -mt-px text-teal-600 dark:text-teal-400" />}
                        <span className="font-medium text-gray-700 dark:text-gray-300">{n.actor_name || 'Jemand'}</span>
                        {' '}{t.types[n.type] || n.type}
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-200 truncate">
                        {n.project_code && n.task_number && (
                          <span className="font-mono text-xs text-gray-400 mr-1">{n.project_code}-{n.task_number}</span>
                        )}
                        {n.task_title}
                      </p>
                      <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
